import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getPurchaseOrder, getPurchaseOrderItem, getPurchaseOrderStatuses, type PurchaseOrderItem, savePurchaseOrder, type PurchaseOrder, getSupplier } from "../../api/erp";
import { MDBBtn, MDBCard, MDBCardBody, MDBInput, MDBInputGroup, MDBTable, MDBTableBody, MDBTableHead, MDBTooltip } from "mdb-react-ui-kit";
import useLocale, { I18N } from "../../util/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import Select, { type OptionType } from "../../component/select";
import { format } from "date-fns";
import Toast from "../../component/toast";
import { useCrumbs } from "../../component/breadcrumbs";
import Autocomplete from "../../component/autocomplete";

const DEFAULT_STATUSES = [
    { value: 'Placed', label: 'Placed' },
    { value: 'Complete', label: 'Complete' },
    { value: 'Deleted', label: 'Deleted' }
];

export default function PurchaseOrder(props: { editable: boolean }) {
    let { 0: query } = useSearchParams();
    let { 0: order, 1: setOrder } = React.useState<PurchaseOrder>({ id: '', supplier: '', order_id: '', ordered_at: Math.round(Date.now() / 1000), delivered_at: 0, received_at: 0, status: 'Placed' });
    let { 0: items, 1: setItems } = React.useState<Array<PurchaseOrderItem>>([]);
    let { 0: statuses, 1: setStatuses } = React.useState<Array<OptionType>>(DEFAULT_STATUSES);
    let { 0: suppliers, 1: setSuppliers } = React.useState<Array<OptionType>>([]);
    let { 0: __ } = useLocale();
    let { 0: setCrumbs } = useCrumbs();
    let navigate = useNavigate();
    let save = React.useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (!(e.target as HTMLFormElement).checkValidity()) {
            return;
        }
        savePurchaseOrder({ ...order, item: items }).then(response => {
            if (typeof response !== 'string' && response.data) {
                let statuses: Array<OptionType> = JSON.parse(window.sessionStorage.getItem('PO-status') ?? '[]');
                if (statuses.filter(p => p.value === order.status).length === 0) {
                    statuses.push({ value: order.status, label: order.status });
                    setStatuses(statuses);
                }
                navigate('/purchase-order/edit?id=' + response.data, { replace: true });
            }
            Toast.show('Data has been saved.');
        }, () => Toast.show(__('An error detected. Please try again later.')));
    }, [order, items, __]);
    let onChangeOrder = React.useCallback((e: { target: { name: string, value: string } } | string) => {
        setOrder(old => {
            if (typeof e === 'string') {
                return { ...old, status: e };
            } else if (e.target.name.endsWith('_at')) {
                return { ...old, [e.target.name]: Date.parse(e.target.value) / 1000 };
            } else {
                return { ...old, [e.target.name]: e.target.value };
            }
        });
    }, [setOrder]);
    let onChangeItem = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setItems(old => {
            const { 0: key, 1: index } = e.target.name.substring(0, e.target.name.length - 1).split('[');
            let cloned = [...old];
            if (key === 'sku') {
                cloned[parseInt(index)][key] = e.target.value;
            } else {
                cloned[parseInt(index)][key as 'ordered_qty' | 'receipt_qty'] = parseFloat(e.target.value);
            }
            return cloned;
        });
    }, [setItems]);
    let add = React.useCallback((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setItems(old => ([...old, { id: '', sku: '', ordered_qty: 0, receipt_qty: 0, ordered_at: order.ordered_at, delivered_at: order.delivered_at, order_id: order.id }]));
    }, [setItems, order]);
    React.useEffect(() => {
        let id = query.get('id');
        if (id) {
            let cached = window.sessionStorage.getItem('PO-' + id);
            if (cached) {
                let order = JSON.parse(cached);
                setOrder(order);
                setCrumbs([{ label: 'Purchase Order', link: '/purchase-order' }, order.order_id]);
            } else {
                getPurchaseOrder({ filter: { id }, limit: 1 }).then(response => {
                    if (typeof response !== 'string' && response.data.length) {
                        setCrumbs([{ label: 'Purchase Order', link: '/purchase-order' }, response.data[0].order_id]);
                        setOrder(response.data[0]);
                    }
                });
            }
            getPurchaseOrderItem({ filter: { order_id: id }, limit: -1 }).then(response => {
                if (typeof response !== 'string') {
                    setItems(response.data);
                }
            });
        } else {
            setCrumbs([{ label: 'Purchase Order', link: '/purchase-order' }, 'Edit']);
        }
        let statuses = window.sessionStorage.getItem('PO-status');
        if (statuses) {
            setStatuses(JSON.parse(statuses));
        } else {
            getPurchaseOrderStatuses().then(response => {
                let result = response.map(item => ({ value: item.status, label: item.status + '(' + item.count + ')' }));
                window.sessionStorage.setItem('PO-status', JSON.stringify(result));
                setStatuses(result);
            });
        }
        let suppliers = window.sessionStorage.getItem('suppliers');
        if (suppliers) {
            setSuppliers(JSON.parse(suppliers));
        } else {
            getSupplier({ limit: -1 }).then(response => {
                if (typeof response !== 'string') {
                    let result = response.data.map(item => ({ value: item.id, label: item.name }));
                    window.sessionStorage.setItem('suppliers', JSON.stringify(result));
                    setSuppliers(result);
                }
            });
        }
    }, [query, setOrder, setItems, props.editable]);
    return (
        <form className="edit h-100" onSubmit={save}>
            <div className="accordion">
                <MDBCard className="mb-3">
                    <MDBCardBody>
                        <input type="hidden" name="id" value={order.id} />
                        <MDBInput label={__('Order ID')} required readOnly={!props.editable} wrapperClass="mb-3" className="active" name="order_id" value={order.order_id} onChange={onChangeOrder} />
                        <Select label={__('Supplier')} readOnly={!props.editable} wrapperClass="mb-3" className="active" name="supplier" value={order.supplier} onChange={onChangeOrder} options={suppliers} />
                        <MDBInput type="date" label={__('Ordered at')} required readOnly={!props.editable} wrapperClass="mb-3" className="active" name="ordered_at" value={order.ordered_at ? format(order.ordered_at * 1000, 'yyyy-MM-dd') : ''} onChange={onChangeOrder} />
                        <MDBInput type="date" label={__('Delivered at')} readOnly={!props.editable} wrapperClass="mb-3" className="active" name="delivered_at" value={order.delivered_at ? format(order.delivered_at * 1000, 'yyyy-MM-dd') : ''} onChange={onChangeOrder} />
                        <MDBInput type="date" label={__('Received at')} readOnly={!props.editable} wrapperClass="mb-3" className="active" name="received_at" value={order.received_at ? format(order.received_at * 1000, 'yyyy-MM-dd') : ''} onChange={onChangeOrder} />
                        <Autocomplete label={__('Status')} required readOnly={!props.editable} name="status" value={order.status} options={statuses?.length ? statuses : DEFAULT_STATUSES} onChange={onChangeOrder} />
                    </MDBCardBody>
                </MDBCard>
                <MDBCard>
                    <MDBCardBody>
                        <MDBTable>
                            <MDBTableHead>
                                <tr>
                                    <th>SKU</th>
                                    <th className="text-nowrap text-center">{__('Ordered Qty')}</th>
                                    <th className="text-nowrap text-center">{__('Receipt Qty')}</th>
                                </tr>
                            </MDBTableHead>
                            {props.editable ? (
                                <>
                                    <MDBTableBody>
                                        {items.map((item, i) => (
                                            <tr key={item.id + i.toString()}>
                                                <th>
                                                    <input type="hidden" name={'id[' + i + ']'} value={item.id} />
                                                    <MDBInput name={'sku[' + i + ']'} value={item.sku} onChange={onChangeItem} />
                                                </th>
                                                <th><MDBInput type="number" min="0" step="0.0001" name={'ordered_qty[' + i + ']'} value={item.ordered_qty} onChange={onChangeItem} /></th>
                                                <th><MDBInput type="number" min="0" step="0.0001" max={item.ordered_qty} name={'receipt_qty[' + i + ']'} value={item.receipt_qty} onChange={onChangeItem} /></th>
                                            </tr>
                                        ))}
                                    </MDBTableBody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={3}>
                                                <MDBBtn color="light" block onClick={add}>
                                                    <FontAwesomeIcon icon={faPlusCircle} size="2x" />
                                                </MDBBtn>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </>
                            ) : (
                                <MDBTableBody>
                                    {items.map(item => (
                                        <tr key={item.id}>
                                            <th>{item.sku}</th>
                                            <th className="text-center">{parseFloat(item.ordered_qty.toString())}</th>
                                            <th className="text-center">{parseFloat(item.receipt_qty.toString())}</th>
                                        </tr>
                                    ))}
                                </MDBTableBody>
                            )}
                        </MDBTable>
                    </MDBCardBody>
                </MDBCard>
            </div>
            {props.editable ? (
                <MDBCard className="tools">
                    <MDBCardBody>
                        <div className="btns">
                            <MDBBtn color="info" type="reset"><I18N>Reset</I18N></MDBBtn>
                            <MDBBtn color="primary" type="submit"><I18N>Save</I18N></MDBBtn>
                        </div>
                    </MDBCardBody>
                </MDBCard>
            ) : null}
        </form>
    );
}